import { AlertTriangle, CheckCircle2, Inbox, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { ISSUE_STATUS, ITEM_STATUS } from "@/lib/format";
import type { IssueStatus, ItemStatus } from "@/lib/types";

/* ------------------------------------------------------------------ badges */

export function Badge({ tone = "", children, plain }: { tone?: string; children: ReactNode; plain?: boolean }) {
  return <span className={`badge ${tone ? `badge--${tone}` : ""} ${plain ? "badge--plain" : ""}`}>{children}</span>;
}

export function ItemStatusBadge({ status }: { status: ItemStatus }) {
  const s = ITEM_STATUS[status] ?? { label: status, tone: "" };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function IssueStatusBadge({ status }: { status: IssueStatus }) {
  const s = ISSUE_STATUS[status] ?? { label: status, tone: "" };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

/* ------------------------------------------------------------------ layout pieces */

export function PageHead({
  eyebrow,
  title,
  sub,
  actions,
  crumbs,
}: {
  eyebrow?: string;
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  crumbs?: { label: string; to?: string }[];
}) {
  return (
    <header className="page-head">
      <div>
        {crumbs && (
          <nav className="crumbs" aria-label="Breadcrumb">
            {crumbs.map((crumb, i) => (
              <span key={crumb.label} className="row" style={{ gap: 6 }}>
                {i > 0 && <span aria-hidden="true">/</span>}
                {crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{crumb.label}</span>}
              </span>
            ))}
          </nav>
        )}
        {eyebrow && <p className="page-head__eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {sub && <p className="page-head__sub">{sub}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </header>
  );
}

export function Card({
  title,
  actions,
  children,
  flush,
  className = "",
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card__head">
          {title && <h2 className="card__title">{title}</h2>}
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      <div className={`card__body ${flush ? "card__body--flush" : ""}`}>{children}</div>
    </section>
  );
}

export function Empty({ title, children, icon }: { title: string; children?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon ?? <Inbox size={24} />}</div>
      <p className="empty__title">{title}</p>
      {children && <div>{children}</div>}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="loading-block" role="status">
      <span className="spinner" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function ErrorBlock({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="alert alert--bad" role="alert">
      <AlertTriangle size={18} />
      <div className="stack" style={{ gap: 6 }}>
        <span>{message}</span>
        {onRetry && (
          <button className="btn btn--sm" onClick={onRetry} type="button" style={{ justifySelf: "start" }}>
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

export function Pager({
  page,
  count,
  pageSize,
  onPage,
}: {
  page: number;
  count: number;
  pageSize: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(count / pageSize));
  if (count === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, count);
  return (
    <div className="pager">
      <span>
        {from}–{to} of {count}
      </span>
      <div className="pager__buttons">
        <button className="btn btn--sm" disabled={page <= 1} onClick={() => onPage(page - 1)} type="button">
          Previous
        </button>
        <button className="btn btn--sm" disabled={page >= pages} onClick={() => onPage(page + 1)} type="button">
          Next
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ form bits */

export function Field({
  label,
  hint,
  error,
  children,
  aside,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">
        <span>{label}</span>
        {aside}
      </span>
      {children}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <span className="check">
      <button
        type="button"
        role="switch"
        className="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
      <span>{label}</span>
    </span>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: T; label: ReactNode }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="tab"
          aria-selected={tab.id === value}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ dialogs */

export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>("input, textarea, select, button")?.focus();
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [onClose]);

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div ref={panel} className={`dialog ${wide ? "dialog--wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="dialog__head">
          <h2 id={titleId} className="dialog__title">
            {title}
          </h2>
          <button className="btn btn--ghost btn--icon btn--sm" onClick={onClose} aria-label="Close" type="button">
            <X size={18} />
          </button>
        </div>
        <div className="dialog__body">{children}</div>
        {footer && <div className="dialog__foot">{footer}</div>}
      </div>
    </div>
  );
}

interface ConfirmOptions {
  title: string;
  body: ReactNode;
  confirm: string;
  danger?: boolean;
}

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

/** `const confirm = useConfirm(); if (await confirm({...})) …` — a styled window.confirm. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
  const ask = useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...options, resolve })),
    [],
  );
  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };
  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {pending && (
        <Dialog
          title={pending.title}
          onClose={() => close(false)}
          footer={
            <>
              <button className="btn" onClick={() => close(false)} type="button">
                Cancel
              </button>
              <button
                className={`btn ${pending.danger ? "btn--danger" : "btn--primary"}`}
                onClick={() => close(true)}
                type="button"
              >
                {pending.confirm}
              </button>
            </>
          }
        >
          <div>{pending.body}</div>
        </Dialog>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const value = useContext(ConfirmContext);
  if (!value) throw new Error("useConfirm must be used inside <ConfirmProvider>.");
  return value;
}

/* ------------------------------------------------------------------ toasts */

interface Toast {
  id: number;
  tone: "ok" | "bad";
  message: string;
}

const ToastContext = createContext<{ ok: (message: string) => void; bad: (message: string) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const push = useCallback((tone: Toast["tone"], message: string) => {
    const id = nextId.current++;
    setToasts((list) => [...list, { id, tone, message }]);
    setTimeout(() => setToasts((list) => list.filter((toast) => toast.id !== id)), tone === "bad" ? 7000 : 3500);
  }, []);
  const value = useMemo(
    () => ({ ok: (m: string) => push("ok", m), bad: (m: string) => push("bad", m) }),
    [push],
  );
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`} role={toast.tone === "bad" ? "alert" : "status"}>
            {toast.tone === "ok" ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside <ToastProvider>.");
  return value;
}

/** Turn any thrown error into a toast message. */
export function errorText(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong.";
}
